import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Crown, LogIn, LogOut, User, Users, ChevronDown, Compass, Mail, KeyRound, Settings, Radio, Layers, Map } from 'lucide-react';
import AuthModal from './AuthModal';

const OWNER_EMAIL = 'info@skaidracreative.co.uk';

const UserMenu = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isVIP, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    // Navigate to home page and force refresh to clear all UI state
    navigate('/');
    window.location.reload();
  };

  const handleNavigate = (path) => {
    setShowDropdown(false);
    navigate(path);
  };

  if (!isAuthenticated) {
    return (
      <>
        <Button
          onClick={() => setShowAuthModal(true)}
          variant="outline"
          size="sm"
          className="border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10 gap-2"
          data-testid="login-button"
        >
          <LogIn className="w-4 h-4" />
          Prisijungti
        </Button>
        
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => setShowAuthModal(false)}
        />
      </>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center gap-2">
        {isVIP && (
          <div className="flex items-center gap-1 px-2 py-1 bg-[#d4af37]/20 rounded-full border border-[#d4af37]/50">
            <Crown className="w-3 h-3 text-[#d4af37]" />
            <span className="text-xs text-[#d4af37] font-semibold">VIP</span>
          </div>
        )}
        
        {/* User Button with Dropdown */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a3e]/50 rounded-full border border-[#d4af37]/30 hover:border-[#d4af37]/60 transition-colors"
          data-testid="user-menu-button"
        >
          <User className="w-4 h-4 text-[#d4af37]" />
          <span className="text-sm text-foreground">{user?.firstName}</span>
          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-56 rounded-lg bg-[#0f0f23] border border-[#d4af37]/40 shadow-xl shadow-black/70 z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: '#0f0f23' }}>
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#d4af37]/20 bg-[#1a1a3e]/50">
            <p className="text-sm font-medium text-foreground">{user?.firstName} {user?.lastName}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          
          {/* Menu Items */}
          <div className="py-1 bg-[#0f0f23]">
            {/* Fortune Chest - FIRST ITEM with special styling */}
            <button
              onClick={() => handleNavigate('/sekmes-skrynia')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-amber-500/20 transition-colors group cursor-pointer"
              data-testid="fortune-chest-link"
            >
              <KeyRound className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-medium">Tavo sėkmės skrynia</span>
            </button>
            
            <button
              onClick={() => handleNavigate('/mano-ratas')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-[#d4af37]/20 transition-colors cursor-pointer"
              data-testid="my-circle-link"
            >
              <Compass className="w-4 h-4 text-[#d4af37]" />
              <span>Mano ratas</span>
            </button>
            
            <button
              onClick={() => handleNavigate('/rezultatai')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-[#d4af37]/20 transition-colors cursor-pointer"
              data-testid="my-analysis-link"
            >
              <User className="w-4 h-4 text-muted-foreground" />
              <span>Mano analizė</span>
            </button>
            
            <button
              onClick={() => handleNavigate('/skaiciu-zemelapis')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-[#d4af37]/20 transition-colors cursor-pointer"
              data-testid="numbers-map-link"
            >
              <Map className="w-4 h-4 text-[#d4af37]" />
              <span>Skaičių žemėlapis</span>
            </button>
            
            <button
              onClick={() => handleNavigate('/ss-metodas')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-[#d4af37]/20 transition-colors cursor-pointer"
              data-testid="ss-method-link"
            >
              <Layers className="w-4 h-4 text-purple-400" />
              <span>SS Metodo Analizė</span>
            </button>
            
            <button
              onClick={() => handleNavigate('/skaiciuoti')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-[#d4af37]/20 transition-colors cursor-pointer"
              data-testid="calculate-link"
            >
              <Users className="w-4 h-4 text-muted-foreground" />
              <span>Skaičiuoti iš naujo</span>
            </button>
            
            <button
              onClick={() => handleNavigate('/profilis')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-[#d4af37]/20 transition-colors cursor-pointer"
              data-testid="profile-settings-link"
            >
              <Settings className="w-4 h-4 text-muted-foreground" />
              <span>Redaguoti profilį</span>
            </button>
            
            {/* Live Skaičiuoklė - ONLY visible for admin */}
            {user?.email?.toLowerCase() === OWNER_EMAIL.toLowerCase() && (
              <button
                onClick={() => handleNavigate('/owner-live')}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-emerald-500/20 transition-colors cursor-pointer"
                data-testid="live-calculator-link"
              >
                <Radio className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Live Skaičiuoklė</span>
              </button>
            )}
          </div>
          
          {/* Numenaxis Naujienos - separate section */}
          <div className="border-t border-[#d4af37]/20 py-1 bg-[#0f0f23]">
            <button
              onClick={() => handleNavigate('/naujienos')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-purple-500/20 transition-colors cursor-pointer"
              data-testid="news-link"
            >
              <Mail className="w-4 h-4 text-purple-400" />
              <span>Numenaxis Naujienos</span>
            </button>
          </div>
          
          {/* Logout */}
          <div className="border-t border-[#d4af37]/20 py-1 bg-[#0f0f23]">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/20 transition-colors cursor-pointer"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>Atsijungti</span>
            </button>
          </div>
        </div>
      )}
      
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
};

export default UserMenu;
